import { getCurrencySymbol, getNumberOfCurrencyDigits } from '@angular/common';
import { AuthenticationService } from '@omni/services/authentication.service';
import { DateTimeFormatsService } from '@omni/services/date-time-formats/date-time-formats.service';
import { Injectable } from '@angular/core';

const ISO_CODE = /^[A-Z]{3}$/;

/**
 * Money for the order capture screens: the app's number format and symbol position, as the
 * legacy order screen renders it.
 *
 * The symbol comes from the price list's ISO currency code (`currencyCode` on the bundle). A
 * value that is not an ISO code — a currency name — falls back to the symbol on the user record,
 * the same source the rest of the app uses. Amounts keep at least the currency's own decimals
 * (KWD 8.650), and a digit the price list's precision carries is never hidden (USD 0.125).
 */
@Injectable({ providedIn: 'root' })
export class AppUiMoneyFormatter {
  constructor(
    private readonly dateTimeFormatsService: DateTimeFormatsService,
    private readonly authenticationService: AuthenticationService,
  ) {}

  /** `precision`: the currency's decimals as Dynamics holds them, which can exceed its ISO digits. */
  public format(
    value: number | null | undefined,
    currency: string | null | undefined,
    precision?: number | null,
  ): string {
    const amount = this.amountText(value ?? 0, currency, precision);
    const symbol = this.symbolFor(currency);
    if (!symbol) return amount;
    return this.dateTimeFormatsService.selectedSymbolPos.position === 'left'
      ? `${symbol}${amount}`
      : `${amount} ${symbol}`;
  }

  private amountText(
    value: number,
    currency: string | null | undefined,
    precision: number | null | undefined,
  ): string {
    const setting =
      this.dateTimeFormatsService.selectedDecimalPos?.toFixed ?? 2;
    const least = Math.max(setting, this.isoDigitsOf(currency) ?? 0);
    const most = Math.max(least, precision ?? 0);
    if (most <= setting)
      return this.dateTimeFormatsService.getFormattedNumber(value);
    const [whole, fraction = ''] = value.toFixed(most).split('.');
    // Org precision 3 on USD would otherwise print $2,767.600.
    const shown =
      fraction.slice(0, least) + fraction.slice(least).replace(/0+$/, '');
    // Grouped on the whole part only: the shared formatter would group a long fraction too.
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return shown ? `${grouped}.${shown}` : grouped;
  }

  private isoDigitsOf(currency: string | null | undefined): number | null {
    return currency && ISO_CODE.test(currency)
      ? getNumberOfCurrencyDigits(currency)
      : null;
  }

  private symbolFor(currency: string | null | undefined): string {
    if (currency && ISO_CODE.test(currency))
      return getCurrencySymbol(currency, 'narrow');
    return this.authenticationService.user?.currencysymbol ?? '';
  }
}
